import { createBrowserHistory, createMemoryHistory } from 'history';
import {
    DigitalDataStrategy,
    AnalyticsMgr,
    BCManager,
    GBDManager,
    IVMgr,
    CoreFlagMgr,
    InteractionMgr,
    RUMMgr,
    FHPManager
} from '@nab/nab-x-sdk-browser';
import {
    AppServicesClosure,
    IAnalyticsMgr,
    ServicesConfig,
    IDataSecurityMgr,
    IAnalyticsStrategy,
    ICoreFlagMgr,
    IFlagStrategy,
    IDataSecurityConfig,
    IRUMMgr,
    IRUMMgrConfig,
    IInteractionMgrConfig,
    IInteractionMgr,
    IInteractionMgrAdditionalConfig,
    IDataSecurityAdditionalConfig
} from '@nab/x-types';
import { getUserConfig } from '../user';

interface AnalyticsInfo {
    strategy: IAnalyticsStrategy;
}

const createAnalyticsMgr: (analyticsInfo: AnalyticsInfo) => IAnalyticsMgr = ({ strategy = null }) =>
    new AnalyticsMgr(strategy || new DigitalDataStrategy());

const createRUMMgr: (rumConfig: IRUMMgrConfig) => IRUMMgr = (rumConfig) => new RUMMgr(rumConfig.strategy);

const createDataSecurityMgr: (dataSecurityInfo: IDataSecurityConfig, additionalConfig?: IDataSecurityAdditionalConfig) => IDataSecurityMgr = (
    {
        iv: { LibraryUrl: ivLib, configValue: ivConfig } = { LibraryUrl: '' },
        gbd: { LibraryUrl: gbdLib, configValue: gbdConfig } = { LibraryUrl: '' },
        bc: { LibraryUrl: bcLib, configValue: bcConfig } = { LibraryUrl: '' },
        fhp: { LibraryUrl: fhpLib, configValue: fhpConfig } = { LibraryUrl: '' }
    },
    additionalConfig
) => {
    // Because the BioCatch's CSID and FHP's CSID must be the same. We need to centralize it here.
    const { id, authenticated, sessionId } = getUserConfig();
    const bcCSID = typeof bcConfig === 'string' ? bcConfig : bcConfig?.csId || sessionId;
    const newBcConfig = typeof bcConfig === 'object' ? { ...bcConfig, csId: bcCSID } : bcCSID;
    const fhpCSID = fhpConfig?.csId || bcCSID;
    return {
        bc: new BCManager(bcLib, newBcConfig, additionalConfig),
        gbd: new GBDManager(gbdLib, gbdConfig),
        iv: new IVMgr(ivLib, ivConfig),
        fhp: new FHPManager(fhpLib, { ...fhpConfig, csId: fhpCSID, userId: id, authenticated })
    };
};

interface FlagMgrInfo {
    strategy: IFlagStrategy;
}
const createFlagMgr: (flagMgrInfo: FlagMgrInfo) => ICoreFlagMgr = ({ strategy = null }) => {
    const flagMgr = new CoreFlagMgr();
    return flagMgr;
};

const createInteractionMgr: (config: IInteractionMgrConfig, additionalConfig?: IInteractionMgrAdditionalConfig) => IInteractionMgr = (
    config,
    additionalConfig
) => {
    const interactionMgr = new InteractionMgr(config, undefined, additionalConfig);
    return interactionMgr;
};

export const defaultServicesProvider = (servicesConfig?: ServicesConfig): AppServicesClosure => ({
    analyticsMgr: () => createAnalyticsMgr((servicesConfig && servicesConfig.analytics) || ({} as AnalyticsInfo)),
    dataSecurityMgr: () =>
        createDataSecurityMgr(
            (servicesConfig && servicesConfig.dataSecurity) ||
                ({ iv: { LibraryUrl: '' }, gbd: { LibraryUrl: '' }, bc: { LibraryUrl: '' }, fhp: { LibraryUrl: '' } } as IDataSecurityConfig),
            servicesConfig?.interactionPrefix?.length > 0 ? { interactionPrefix: servicesConfig.interactionPrefix } : undefined
        ),
    flagMgr: () => createFlagMgr((servicesConfig && servicesConfig.flags) || ({} as FlagMgrInfo)),
    history: () =>
        servicesConfig?.history?.suppressRouting
            ? createMemoryHistory()
            : createBrowserHistory({
                  basename: servicesConfig?.history?.routingBasePath || ''
              }),
    interactionMgr: () => {
        if (servicesConfig) {
            return createInteractionMgr(servicesConfig.interaction, { interactionPrefix: servicesConfig.interactionPrefix });
        }
        return createInteractionMgr({} as IInteractionMgrConfig);
    },
    rumMgr: () => createRUMMgr((servicesConfig && servicesConfig.rum) || ({} as IRUMMgrConfig))
});
